import React, { createContext, useContext, useMemo } from 'react';
import {
	IDeleteSSOConfigDialogResult,
	IScimRevokeActionsDialogResult,
	ISaveConfirmDialogResult,
	ISaveUntestedDialogResult,
	IUnsavedNavigationDialogResult,
	IVerifyDomainDialogResult
} from 'types/dialogs';
import { ScimConfirmActionType } from './ScimRevokeActionsDialog/ScimRevokeActionsDialog';
import useScimRevokeActionsDialog from './ScimRevokeActionsDialog/useScimRevokeActionsDialog';
import useSaveConfirmDialog from './SaveConfirmDialog/useSaveConfirmDialog';
import useSaveUntestedDialog from './SaveUntestedDialog/useSaveUntestedDialog';
import useUnsavedNavigationDialog from './UnsavedNavigationDialog/useUnsavedNavigationDialog';
import useVerifyDomainDialog from './VerifyDomainDialog/useVerifyDomainDialog';
import useDeleteSSOConfigDialog from './DeleteSSOConfigDialog/useDeleteSSOConfigDialog';

interface IDialogsContextValue {
	openVerifyDomainDialog: (
		initialDomain: string
	) => Promise<IVerifyDomainDialogResult>;
	openScimRevokeActionsDialog: (
		actionType: ScimConfirmActionType
	) => Promise<IScimRevokeActionsDialogResult>;
	openUnsavedNavigationDialog: () => Promise<IUnsavedNavigationDialogResult>;
	openSaveUntestedDialog: () => Promise<ISaveUntestedDialogResult>;
	openSaveConfirmDialog: () => Promise<ISaveConfirmDialogResult>;
	openDeleteSSOConfigDialog: () => Promise<IDeleteSSOConfigDialogResult>;
}

const DialogsContext = createContext<IDialogsContextValue | undefined>(
	undefined
);

interface IDialogsContextProviderProps {
	children: React.ReactNode;
}

export const DialogsContextProvider: React.FC<IDialogsContextProviderProps> = ({
	children
}) => {
	const { verifyDomainDialogJsx, openVerifyDomainDialog } =
		useVerifyDomainDialog();
	const { ScimRevokeActionsDialogJsx, openScimRevokeActionsDialog } =
		useScimRevokeActionsDialog();
	const { unsavedNavigationDialogJsx, openUnsavedNavigationDialog } =
		useUnsavedNavigationDialog();
	const { saveUntestedDialogJsx, openSaveUntestedDialog } =
		useSaveUntestedDialog();
	const { saveConfirmDialogJsx, openSaveConfirmDialog } =
		useSaveConfirmDialog();
	const { deleteSSOConfigDialogJsx, openDeleteSSOConfigDialog } =
		useDeleteSSOConfigDialog();

	const value = useMemo(
		() => ({
			openVerifyDomainDialog,
			openScimRevokeActionsDialog,
			openUnsavedNavigationDialog,
			openSaveUntestedDialog,
			openSaveConfirmDialog,
			openDeleteSSOConfigDialog
		}),
		[
			openVerifyDomainDialog,
			openScimRevokeActionsDialog,
			openUnsavedNavigationDialog,
			openSaveUntestedDialog,
			openSaveConfirmDialog,
			openDeleteSSOConfigDialog
		]
	);

	return (
		<DialogsContext.Provider value={value}>
			{verifyDomainDialogJsx}
			{ScimRevokeActionsDialogJsx}
			{unsavedNavigationDialogJsx}
			{saveUntestedDialogJsx}
			{saveConfirmDialogJsx}
			{deleteSSOConfigDialogJsx}
			{children}
		</DialogsContext.Provider>
	);
};

export const useDialogsContext = (): IDialogsContextValue => {
	/* istanbul ignore if */
	const context = useContext(DialogsContext);
	if (!context) throw new Error('DialogsContextProvider is missing');
	return context;
};
